/**
 * Pure scoring + progress helpers for the cleaning inspection feature.
 *
 * Why this lives here (not in CleaningInspection.js):
 *   These three functions have no React/component dependencies. They take a
 *   plain inspection object and the checklist shape; the rest is arithmetic.
 *   Extracting them keeps the page component focused on rendering and event
 *   handling, makes them trivially unit-testable, and trims ~70 lines off
 *   the 2,300-line page file.
 */

/**
 * Calculate the percentage of inspection items that have been checked
 * (i.e. given a non-null status: pass / fail / na).
 *
 * @param {object} inspection — must have `checklist` keyed by category → itemId.
 * @param {object} inspectionChecklist — schema: { [category]: { items: [{id, ...}] } }
 * @returns {number} 0-100 inclusive.
 */
export function calculateProgress(inspection, inspectionChecklist) {
  if (!inspection?.checklist) return 0;
  let total = 0;
  let checked = 0;
  for (const category of Object.keys(inspectionChecklist)) {
    for (const item of inspectionChecklist[category].items) {
      total++;
      if (inspection.checklist[category]?.[item.id]?.status !== null) {
        checked++;
      }
    }
  }
  return total > 0 ? Math.round((checked / total) * 100) : 0;
}

/**
 * Calculate the inspection's overall pass rate as a percentage. Items marked
 * "na" are excluded from both numerator and denominator. Unchecked items are
 * ignored — call {@link calculateProgress} first if you also need completion.
 *
 * @returns {number|null} 0-100, or null if nothing scoreable yet.
 */
export function calculateScore(inspection, inspectionChecklist) {
  if (!inspection?.checklist) return null;
  let passed = 0;
  let total = 0;
  for (const category of Object.keys(inspectionChecklist)) {
    for (const item of inspectionChecklist[category].items) {
      const result = inspection.checklist[category]?.[item.id];
      if (result?.status && result.status !== 'na') {
        total++;
        if (result.status === 'pass') passed++;
      }
    }
  }
  return total > 0 ? Math.round((passed / total) * 100) : null;
}

/**
 * Count items in a single category by their current status.
 * Used by the bulk-toolbar pills above each section.
 */
export function getSectionCounts(inspection, inspectionChecklist, category) {
  const empty = { pass: 0, fail: 0, na: 0, unchecked: 0 };
  if (!inspection?.checklist || !inspectionChecklist[category]) return empty;
  const counts = { ...empty };
  for (const item of inspectionChecklist[category].items) {
    const status = inspection.checklist[category]?.[item.id]?.status;
    if (status === 'pass') counts.pass++;
    else if (status === 'fail') counts.fail++;
    else if (status === 'na') counts.na++;
    else counts.unchecked++;
  }
  return counts;
}

/**
 * Map a 0-100 percentage score to a 1-5 star rating using the project's
 * historical buckets (≥90 → 5★, ≥80 → 4★, ≥70 → 3★, ≥60 → 2★, else 1★).
 */
export function scoreToRating(score) {
  if (score >= 90) return 5;
  if (score >= 80) return 4;
  if (score >= 70) return 3;
  if (score >= 60) return 2;
  return 1;
}
